import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { PageShell, Panel } from "../components/PageShell";
import { TOOLS } from "../data/tools";
import { CATEGORIES } from "../data/categories";

const NAMES: Record<string, string> = { ...Object.fromEntries(TOOLS.map((t) => [t.id, t.name])), ...Object.fromEntries(CATEGORIES.map((c) => [c.id, c.name])), group: "Group votes", wheel: "Custom wheels" };
const named = (rows: Row[]) => rows.map((r) => ({ ...r, key: NAMES[r.key] || r.key }));

interface Row { key: string; count: number }
interface Summary {
  days: number;
  totals: { visitors: number; pageviews: number; decisions: number; search_share: number };
  daily: { day: string; views: number; visitors: number }[];
  top_pages: Row[]; landing_pages: Row[]; sources: Row[]; referrers: Row[]; search_landing_pages: Row[]; top_tools: Row[];
  shares: number; groups_created: number;
}

function Stat({ label, value, testId }: { label: string; value: string | number; testId: string }) {
  return (
    <div className="rounded-2xl border-[3px] border-[#F2D9B8] bg-white p-3 text-center" data-testid={testId}>
      <p className="ua-logo text-3xl sm:text-4xl">{value}</p>
      <p className="text-[11px] font-extrabold uppercase tracking-widest text-[#8a5a2b]">{label}</p>
    </div>
  );
}

function Table({ title, rows, testId, empty = "No data yet" }: { title: string; rows: Row[]; testId: string; empty?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel className="!p-3">
      <h2 className="mb-2 font-comic text-base uppercase text-[#3b1800]">{title}</h2>
      <ul className="space-y-1" data-testid={testId}>
        {rows.length ? rows.map((r) => (
          <li key={r.key} className="relative flex justify-between gap-2 overflow-hidden rounded-lg px-2 py-1 text-sm font-semibold">
            <span className="absolute inset-y-0 left-0 bg-[#FFE14D]/60" style={{ width: `${(r.count / max) * 100}%` }} />
            <span className="relative truncate">{r.key}</span><span className="relative font-comic">{r.count}</span>
          </li>
        )) : <li className="text-sm text-zinc-500">{empty}</li>}
      </ul>
    </Panel>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState("");
  useSeo({ title: "Traffic | DITHER", description: "DITHER traffic analytics.", noindex: true });

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    setError("");
    api.get(`/analytics/summary`, { params: { days } }).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [user, days]);

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return <PageShell title="Traffic" testId="analytics-page"><Panel><p className="font-bold" data-testid="analytics-admin-only">Admins only. <Link className="underline" to="/auth">Sign in as admin</Link>.</p></Panel></PageShell>;
  }
  return (
    <PageShell title="Traffic" subtitle="Which pages and tools bring people in." back="/account" testId="analytics-page">
      <div className="mb-3 flex gap-2">
        {[7, 30, 90].map((d) => <button key={d} className="chip" data-active={days === d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}>{d} days</button>)}
      </div>
      {error && <p className="font-bold text-[#BE123C]" data-testid="analytics-error">{error}</p>}
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Visitors" value={data.totals.visitors} testId="analytics-visitors" />
            <Stat label="Page views" value={data.totals.pageviews} testId="analytics-pageviews" />
            <Stat label="Decisions" value={data.totals.decisions} testId="analytics-decisions" />
            <Stat label="From search" value={`${data.totals.search_share}%`} testId="analytics-search-share" />
          </div>
          <Panel className="!p-3">
            <h2 className="mb-2 font-comic text-base uppercase text-[#3b1800]">Daily visitors</h2>
            <div className="h-48" data-testid="analytics-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily}>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
                  <Tooltip />
                  <Bar dataKey="visitors" fill="#FF5D1F" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <div className="grid gap-3 md:grid-cols-2">
            <Table title="Top pages" rows={data.top_pages} testId="analytics-top-pages" />
            <Table title="Most-used tools & categories" rows={named(data.top_tools)} testId="analytics-top-tools" />
            <Table title="Traffic sources" rows={data.sources} testId="analytics-sources" />
            <Table title="Search engines & referrers" rows={data.referrers} testId="analytics-referrers" />
            <Table title="Landing pages from search" rows={data.search_landing_pages} testId="analytics-search-landings" empty="No search visits yet" />
            <Table title="All landing pages" rows={data.landing_pages} testId="analytics-landing-pages" />
          </div>
          <p className="text-xs font-semibold text-[#8a5a2b]" data-testid="analytics-extra">{data.shares} shares · {data.groups_created} group votes created · bots and link-preview crawlers excluded</p>
        </div>
      )}
    </PageShell>
  );
}
